/**
 * PricingTable Component
 * Displays Core, Core+, and Pro bundle cards with features and CTAs
 */

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Stack,
  useTheme,
  alpha,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Star as StarIcon,
  Rocket as RocketIcon,
} from '@mui/icons-material';
import { BUNDLES, getBundleSkus, calculateBundleTotal, formatPrice, type Bundle, type Sku } from '../../lib/catalog';
import { useCartStore } from '../../store/cart';
import { trackCartAdd } from '../../lib/analytics';

interface PricingTableProps {
  onBundleAdd?: (bundleId: string) => void;
}

const PricingTable: React.FC<PricingTableProps> = ({ onBundleAdd }) => {
  const theme = useTheme();
  const addToCart = useCartStore(state => state.add);

  const handleAddBundle = (bundleId: string) => {
    const skus = getBundleSkus(bundleId);
    skus.forEach(sku => {
      addToCart(sku.id, 1);
      trackCartAdd(sku.id, sku.name, 1, sku.unitAmount);
    });
    onBundleAdd?.(bundleId);
  };

  // Get all features for display
  const getBundleFeatures = (bundle: Bundle): string[] => {
    const skus = getBundleSkus(bundle.id);
    const features: string[] = [];
    
    skus.forEach(sku => {
      if (sku.features) {
        features.push(...sku.features.slice(0, 3)); // Take top 3 features from each
      }
    });
    
    return features.slice(0, 8); // Limit total features shown
  };

  // Calculate monthly cost for display
  const getMonthlyText = (bundle: Bundle): string | null => {
    const skus = getBundleSkus(bundle.id);
    const subscription = skus.find(sku => sku.type === 'subscription');
    if (subscription) {
      return `+ ${formatPrice(subscription.unitAmount)}/mo`;
    }
    return null;
  };

  const bundleConfigs = [
    {
      bundle: BUNDLES.core,
      icon: <CheckIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.info.main,
      description: 'Perfect for getting started with a professional web presence',
    },
    {
      bundle: BUNDLES.core_plus,
      icon: <StarIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.warning.main,
      description: 'Best value for businesses ready to scale their outreach',
      recommended: true,
    },
    {
      bundle: BUNDLES.pro,
      icon: <RocketIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.success.main,
      description: 'Complete package with ongoing support and compliance',
    },
  ];

  return (
    <Box sx={{ py: 4 }}>
      <Typography
        variant="body2"
        color="text.secondary"
        textAlign="center"
        sx={{ mb: 4 }}
      >
        By purchasing, you agree deliverables & SLAs are defined in the SOW and Capabilities Matrix.
      </Typography>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={3}
        justifyContent="center"
        alignItems="stretch"
      >
        {bundleConfigs.map(({ bundle, icon, color, description, recommended }) => {
          const total = calculateBundleTotal(bundle.id);
          const monthlyText = getMonthlyText(bundle);
          const features = getBundleFeatures(bundle);

          return (
            <Card
              key={bundle.id}
              sx={{
                flex: 1,
                maxWidth: 400,
                position: 'relative',
                border: recommended
                  ? `2px solid ${color}`
                  : `1px solid ${theme.palette.divider}`,
                transform: recommended ? 'scale(1.02)' : 'none',
                boxShadow: recommended ? theme.shadows[8] : theme.shadows[2],
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: recommended ? 'scale(1.04)' : 'scale(1.02)',
                  boxShadow: theme.shadows[8],
                },
              }}
            >
              {recommended && (
                <Chip
                  label="MOST POPULAR"
                  color="warning"
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: -12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontWeight: 'bold',
                  }}
                />
              )}

              {bundle.savings && (
                <Chip
                  label={`Save ${bundle.savings}%`}
                  color="success"
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                  }}
                />
              )}

              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 2,
                    color: color,
                  }}
                >
                  {icon}
                  <Typography variant="h5" fontWeight="bold">
                    {bundle.name}
                  </Typography>
                </Box>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3, minHeight: 40 }}
                >
                  {description}
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="h3" fontWeight="bold" color="text.primary">
                    {formatPrice(total)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    one-time setup
                    {monthlyText && (
                      <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                        {monthlyText} maintenance
                      </Box>
                    )}
                  </Typography>
                </Box>

                <Button
                  variant={recommended ? 'contained' : 'outlined'}
                  fullWidth
                  size="large"
                  onClick={() => handleAddBundle(bundle.id)}
                  sx={{
                    mb: 3,
                    py: 1.5,
                    bgcolor: recommended ? color : 'transparent',
                    borderColor: color,
                    color: recommended ? 'white' : color,
                    '&:hover': {
                      bgcolor: recommended
                        ? alpha(color, 0.9)
                        : alpha(color, 0.1),
                      borderColor: color,
                    },
                  }}
                >
                  Add to Cart
                </Button>

                <List dense>
                  {features.map((feature, index) => (
                    <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckIcon sx={{ fontSize: 18, color: 'success.main' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={feature}
                        primaryTypographyProps={{
                          variant: 'body2',
                          color: 'text.secondary',
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
};

export default PricingTable;
